import { type RefObject, useEffect } from 'react';
import { useLatestRef } from './use-latest-ref';

type Target = EventTarget | RefObject<EventTarget | null> | null | undefined;

/**
 * Listens for `type` on `target` while `enabled`. The latest `handler` is always called, so it
 * doesn't need memoising. A ref target is read when the listener attaches.
 */
export function useEventListener<K extends keyof WindowEventMap>(
  target: Window,
  type: K,
  handler: (event: WindowEventMap[K]) => void,
  enabled?: boolean,
): void;
export function useEventListener<K extends keyof DocumentEventMap>(
  target: Document,
  type: K,
  handler: (event: DocumentEventMap[K]) => void,
  enabled?: boolean,
): void;
export function useEventListener<K extends keyof HTMLElementEventMap>(
  target: RefObject<HTMLElement | null>,
  type: K,
  handler: (event: HTMLElementEventMap[K]) => void,
  enabled?: boolean,
): void;
export function useEventListener(
  target: Target,
  type: string,
  handler: (event: Event) => void,
  enabled?: boolean,
): void;
export function useEventListener(
  target: Target,
  type: string,
  handler: (event: Event) => void,
  enabled = true,
): void {
  const handlerRef = useLatestRef(handler);

  useEffect(() => {
    const element = target && 'current' in target ? target.current : target;
    if (!enabled || !element) {
      return;
    }

    const listener = (event: Event) => handlerRef.current(event);
    element.addEventListener(type, listener);
    return () => element.removeEventListener(type, listener);
  }, [target, type, enabled]);
}
